/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-nested-ternary */
import { ActionIcon, Badge, Divider, Group, Paper, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconBriefcase,
  IconCheck,
  IconCoin,
  IconEdit,
  IconEye,
  IconPlus,
  IconRuler2,
  IconScale,
  IconShare,
  IconZoomMoney,
} from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import ProfileCardLink from '../../../../components/core/ProfileCardLink/ProfileCardLink';
import { useProposalServiceContext } from '../../../../contexts/core/proposals/ProposalService.context';
import { EntityTypeType, UnitOfMeasureType } from '../../../../models/core/cache.type';
import { Action } from '../../../../models/core/core.type';
import { ProposalServiceQuotationType, ProposalServiceType } from '../../../../models/core/proposals.type';
import { EntityItemEnum, Feature } from '../../../../utils/constants.utils';
import { formatCurrency } from '../../../../utils/formatter.utils';
import { isNullOrEmpty, newGuid } from '../../../../utils/helper.utils';
import DeleteConfirmPopover from '../../../../components/DeleteConfirmPopover';
import ServiceQuotationModal from './ServiceQuotationModal';

type ServiceQuotationSummaryProps = {
  data: ProposalServiceType;
  isEdit?: boolean;
  sharedResidues?: string[];
  referenceData?: {
    entityTypeData?: EntityTypeType[];
    unitOfMeasures: UnitOfMeasureType[];
  };
  zIndex?: number;
  onAdd?: () => void;
  onSetPrimary?: (quotation: ProposalServiceQuotationType) => void;
  onQuotationAction: (quotation: ProposalServiceQuotationType) => void;
  onToleranceAction: (quotation: ProposalServiceQuotationType) => void;
  onDeleteQuotation?: (quotation: ProposalServiceQuotationType) => void;
  onQuotationSubmit?: (
    quotation: ProposalServiceQuotationType | null,
    action: string,
    confirmed: boolean
  ) => void;
  initialQuotationItem?: ProposalServiceQuotationType | null;
  initialQuotationTab?: 'Cotacao' | 'Tolerancias';
  autoOpenQuotationModal?: boolean;
};

export default function ServiceQuotationSummary(props: ServiceQuotationSummaryProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { proposalServiceData } = useProposalServiceContext();
  const isAddQuotationDisabled: boolean = proposalServiceData
    ? !proposalServiceData.servico ||
      !proposalServiceData.idEntidadeEndereco ||
      (proposalServiceData.tipo !== EntityItemEnum.Service &&
        !proposalServiceData.acondicionamento &&
        !proposalServiceData.equipamento)
    : !props.data.servico;

  const visibleQuotations = [...props.data.cotacoes].filter((x) => x.action !== Action.Delete);
  const [selectedQuotation, setSelectedQuotation] = useState<ProposalServiceQuotationType | null>(null);
  const [quotationModalOpened, setQuotationModalOpened] = useState(false);
  const [defaultActiveKey, setDefaultActiveKey] = useState('Cotacao');
  const canUseDirectModal = !!props.referenceData && props.zIndex !== undefined;
  const autoOpenHandledRef = useRef(false);

  const buildQuotationKey = (quotation: ProposalServiceQuotationType | null | undefined) =>
    quotation?.id || quotation?.idPropostaServicoCotacao?.toString() || quotation?.idTemporaria || '';

  const selectedQuotationKey = buildQuotationKey(selectedQuotation);
  const currentSelectedQuotation =
    visibleQuotations.find((quotation) => buildQuotationKey(quotation) === selectedQuotationKey) ||
    selectedQuotation;

  const openQuotationModal = (
    quotation: ProposalServiceQuotationType | null,
    quotationTab: 'Cotacao' | 'Tolerancias'
  ) => {
    setSelectedQuotation(quotation);
    setDefaultActiveKey(quotationTab);
    setQuotationModalOpened(true);
  };

  useEffect(() => {
    if (!canUseDirectModal || !props.autoOpenQuotationModal || autoOpenHandledRef.current) {
      return;
    }

    autoOpenHandledRef.current = true;
    openQuotationModal(props.initialQuotationItem || null, props.initialQuotationTab || 'Cotacao');
  }, [
    canUseDirectModal,
    props.autoOpenQuotationModal,
    props.initialQuotationItem,
    props.initialQuotationTab,
  ]);

  const buildSupplier = (quotation: ProposalServiceQuotationType) => {
    if (!quotation.idFornecedor) {
      return (
        <Text size="xs" c="dimmed">
          Fornecedor indefinido
        </Text>
      );
    }

    if (quotation.fornecedorCNPJ) {
      return (
        <ProfileCardLink
          id={quotation.idFornecedor.toString()}
          name={quotation.fornecedorNomeFantasia || quotation.fornecedorRazaoSocial || '-'}
          nameSize="xs"
          description={
            quotation.fornecedorNomeFantasia ? quotation.fornecedorRazaoSocial : quotation.fornecedorCNPJ
          }
          descriptionSize="xs"
          descriptionLength={250}
          linkPrefix="entities"
          showLink={false}
        />
      );
    }

    return (
      <ProfileCardLink
        id={quotation.idFornecedor.toString()}
        name={quotation.fornecedorNome || '-'}
        nameSize="xs"
        description={quotation.fornecedorCPF}
        descriptionSize="xs"
        linkPrefix="entities"
        showLink={false}
      />
    );
  };

  return (
    <Paper p="md" withBorder radius="lg">
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <IconZoomMoney size={16} />
            <Text fw={600}>{props.data.servico} - Cotações</Text>
          </Group>
          {props.isEdit && props.onAdd && (
            <Tooltip label="Adicionar Cotação" withArrow>
              <ActionIcon
                size="sm"
                variant="light"
                color="blue"
                onClick={() => {
                  if (canUseDirectModal && props.onQuotationSubmit) {
                    openQuotationModal(null, 'Cotacao');
                    return;
                  }
                  props.onAdd?.();
                }}
                disabled={isAddQuotationDisabled}
              >
                <IconPlus size={15} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        <Group grow align="stretch">
          {visibleQuotations.map((quotation) => {
            const ItemIcon = props.data.idResiduoAcondicionamento
              ? Feature.Reference.Packaging.icon
              : props.data.idResiduoEquipamento
              ? Feature.Reference.Equipment.icon
              : Feature.Reference.Entity.icon;

            const actionLabel = props.isEdit ? 'Editar cotação' : 'Ver cotação';
            const toleranceLabel = props.isEdit ? 'Editar tolerâncias' : 'Ver tolerâncias';
            const ActionIconComponent = props.isEdit ? IconEdit : IconEye;
            const isInvalidQuotation =
              quotation.idServico !== props.data.idServico ||
              quotation.idResiduoAcondicionamento !== props.data.idResiduoAcondicionamento ||
              quotation.idResiduoEquipamento !== props.data.idResiduoEquipamento ||
              quotation.servico !== props.data.servico;

            return (
              <Paper
                key={quotation.id || quotation.idPropostaServicoCotacao || newGuid()}
                p="sm"
                withBorder
                radius="lg"
                style={{
                  width: '100%',
                  backgroundColor: isInvalidQuotation
                    ? '#fff1f0'
                    : quotation.primario
                    ? '#f8fff9'
                    : '#fcfcfd',
                  borderColor: isInvalidQuotation ? '#f08c82' : quotation.primario ? '#b7e4c7' : undefined,
                }}
              >
                <Stack gap={6}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Group gap={4} className="custom-mantine-badge-group-down">
                      <ItemIcon size={14} />
                      {props.data.gerenciar ? <IconBriefcase size={14} /> : <></>}
                      {props.data.compartilhado ? <IconShare size={14} /> : <></>}
                      <Badge variant="outline" size="sm">
                        {props.data.residuoAcondicionamento || props.data.residuoEquipamento || 'Fornecedor'}
                      </Badge>
                      {isInvalidQuotation && (
                        <Badge color="red" variant="light" size="sm">
                          Inválida
                        </Badge>
                      )}
                      {quotation.primario && (
                        <Badge color="green" variant="light" size="sm">
                          Primária
                        </Badge>
                      )}
                    </Group>
                    <Group gap={4}>
                      <IconScale size={14} />
                      <Badge variant="outline" size="sm">
                        {quotation.tolerancias.length}
                      </Badge>
                    </Group>
                  </Group>

                  <Divider />

                  <Group justify="space-between">
                    <Group gap={4}>
                      <IconCoin size={14} />
                      <Text size="xs" fw={500}>
                        {formatCurrency(quotation.precificacao.preco)}
                      </Text>
                    </Group>
                    <Group gap={4}>
                      <IconRuler2 size={14} />
                      <Text size="xs">{`${quotation.precificacao?.quantidade} ${quotation.precificacao?.quantidadeUnidadeMedida} / ${quotation.precificacao?.frequenciaUnidadeMedida}`}</Text>
                    </Group>
                  </Group>

                  <Divider />

                  {buildSupplier(quotation)}

                  <Group justify="space-between">
                    <Group gap={4}>
                      <IconCoin size={14} />
                      <Text size="xs" c="red" fw={500}>
                        {isNullOrEmpty(quotation.preco) ? '-' : formatCurrency(quotation.preco!)}
                      </Text>
                    </Group>
                    <Group gap={3}>
                      <IconRuler2 size={14} />
                      <Text size="xs">
                        {isNullOrEmpty(quotation.quantidade)
                          ? '-'
                          : `${`${quotation.quantidade} `} ${quotation.frequenciaUnidadeMedida1} / ${
                              quotation.frequenciaUnidadeMedida2
                            }`}
                      </Text>
                    </Group>
                  </Group>

                  {/* {quotation.observacao && (
                    <>
                      <Divider variant={props.isEdit ? 'dotted' : 'dashed'} />
                      <Text size="xs" c="dimmed">
                        {quotation.observacao}
                      </Text>
                    </>
                  )} */}

                  {!!props.sharedResidues?.length && (
                    <>
                      <Divider my="sm" variant="dashed" />
                      <Group>
                        {props.sharedResidues.map((item) => (
                          <Badge key={item} variant="outline" size="sm">
                            {item}
                          </Badge>
                        ))}
                      </Group>
                    </>
                  )}

                  <Divider />

                  <Group justify="flex-end" gap={6}>
                    {props.isEdit && !quotation.primario && props.onSetPrimary && (
                      <Tooltip label="Definir como primária" withArrow>
                        <ActionIcon
                          size="sm"
                          color="green"
                          variant="light"
                          onClick={() => props.onSetPrimary?.(quotation)}
                        >
                          <IconCheck size={15} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <Tooltip label={actionLabel} withArrow>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        onClick={() => {
                          if (canUseDirectModal) {
                            openQuotationModal(quotation, 'Cotacao');
                            return;
                          }

                          props.onQuotationAction(quotation);
                        }}
                      >
                        <ActionIconComponent size={15} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={toleranceLabel} withArrow>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="grape"
                        onClick={() => {
                          if (canUseDirectModal) {
                            openQuotationModal(quotation, 'Tolerancias');
                            return;
                          }

                          props.onToleranceAction(quotation);
                        }}
                      >
                        <IconScale size={15} />
                      </ActionIcon>
                    </Tooltip>
                    {props.isEdit && props.onDeleteQuotation && (
                      <DeleteConfirmPopover
                        label="Excluir cotação"
                        message="Esta ação removerá a cotação do serviço."
                        onConfirm={() => props.onDeleteQuotation?.(quotation)}
                      />
                    )}
                  </Group>
                </Stack>
              </Paper>
            );
          })}
        </Group>
      </Stack>

      {props.referenceData && props.zIndex !== undefined && (
        <ServiceQuotationModal
          opened={quotationModalOpened}
          onClose={() => {
            setQuotationModalOpened(false);
            setSelectedQuotation(null);
            setDefaultActiveKey('Cotacao');
          }}
          modoEdicao={!!props.isEdit}
          referenceData={props.referenceData}
          serviceData={props.data}
          origItem={currentSelectedQuotation || null}
          item={currentSelectedQuotation || null}
          defaultActiveKey={defaultActiveKey}
          zIndex={props.zIndex}
          callback={(item, action, confirmed) => {
            if (!confirmed || !props.onQuotationSubmit) {
              return;
            }

            props.onQuotationSubmit(item, action, confirmed);
          }}
        />
      )}
    </Paper>
  );
}
